import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { PortalLayout } from '@/layouts/portal-layout';
import { OverviewPage } from '@/pages/overview';
import { RepositoriesPage } from '@/pages/repositories';
import { SyncIssuesPage } from '@/pages/sync-issues';
import { ProjectPage } from '@/pages/project';
import { ProjectBrokenPage } from '@/pages/project-broken';
import { ProjectArchivedPage } from '@/pages/project-archived';
import { SearchPage } from '@/pages/search';
import { SettingsPage } from '@/pages/settings';
import { NotFoundPage } from '@/pages/not-found';
import { LoginPage } from '@/pages/login';

// RFC-0011 Task 3.2 — code-split decision: `/search` is kept in the MAIN chunk
// (static import), NOT React.lazy-split. Measured: lazy-splitting the route
// trimmed the main chunk by only ~6.9 kB raw / ~1.9 kB gzip (505.1 → 503.2 kB
// gzip, 0.38%) because the globally-mounted Cmd-K palette already pulls in every
// primitive `/search` reuses (useSearch, SearchResultItem, RepoScopeToggle, the
// highlight parser, phosphor icons) — so the split-out module was only ~7 kB and
// did not move the main chunk toward RFC-0010's 500 kB budget. Not worth a
// Suspense boundary + an extra request on first /search visit.

// RFC-0012 Task 2.3 — code-split decision: `/graph` IS lazy-split (the app's
// FIRST React.lazy route). Unlike `/search`, GraphView pulls in the heavy graph
// stack (reagraph + three + @react-three/* + troika), which would regress the
// main chunk for every route. `React.lazy` + the <Suspense> boundary below keep
// that chain in a separate `/graph` chunk, fetched only on first visit.
const GraphView = lazy(() => import('@/pages/GraphView'));

/**
 * Redirect the legacy `/repositories/:repoId[/:docId]` reader routes (RFC-0009's
 * mock doc-reader) to the canonical `/projects/:repo` reader (RFC-0010 §1, O2).
 */
function LegacyRepoRedirect() {
  const { repoId } = useParams();
  return <Navigate to={`/projects/${repoId ?? ''}`} replace />;
}

/** Suspense fallback shown while the lazy `/graph` chunk downloads (RFC-0012). */
function GraphRouteFallback() {
  return (
    <div
      role="status"
      aria-label="Loading dependency graph"
      className="h-[calc(100vh-13rem)] min-h-[480px] animate-pulse rounded-xl border border-border bg-surface"
    />
  );
}

export function App() {
  return (
    <Routes>
      {/* Unauthenticated entry point — rendered OUTSIDE the portal shell. */}
      <Route path="login" element={<LoginPage />} />
      <Route element={<PortalLayout />}>
        <Route index element={<OverviewPage />} />
        <Route path="repositories" element={<RepositoriesPage />} />
        {/* Canonical doc reader (RFC-0010). */}
        <Route path="projects/:repo" element={<ProjectPage />} />
        <Route path="projects/:repo/docs/*" element={<ProjectPage />} />
        {/* State pages — broken-manifest error + archived tombstone (RFC-0010 §8/§9). */}
        <Route path="projects/:repo/broken" element={<ProjectBrokenPage />} />
        <Route path="projects/:repo/archived" element={<ProjectArchivedPage />} />
        {/* Legacy reader routes → redirect to the canonical project reader. */}
        <Route path="repositories/:repoId" element={<LegacyRepoRedirect />} />
        <Route path="repositories/:repoId/:docId" element={<LegacyRepoRedirect />} />
        <Route path="sync-issues" element={<SyncIssuesPage />} />
        <Route path="search" element={<SearchPage />} />
        {/* First lazy route (RFC-0012 §2): Suspense boundary while the graph
            chunk loads on first visit to /graph. */}
        <Route
          path="graph"
          element={
            <Suspense fallback={<GraphRouteFallback />}>
              <GraphView />
            </Suspense>
          }
        />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
